import { FlexBox } from '@fable/components'
import { css, cx } from '@fable/theme'
import { CompChildren, CompProps } from '@fable/types'
import { BoxTextProps, StatsProfileBoxText } from './StatsProfileBoxText'
import { StatsBoxProvider, useStatsBox } from '../../../lib/ctx/stats_box'
import { useGSAP } from '@gsap/react'
import { gsap } from 'gsap'
import React, { useEffect, useRef, useState } from 'react'
import { useStats } from '../../../lib/ctx/stats'
import { useStatsPermission } from '../../../lib/hooks'
import { StatsScreenshotButton } from '../screenshot/StatsScreenshotButton'
import { Element } from 'react-scroll'
import { useStatsTheme } from '../../../theme/ctx'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  text: BoxTextProps
  textAnim?: {
    trigger: 'view' | 'data'
    loaded?: boolean
  }
  actions?: CompChildren
  footer?: CompChildren
  screenshot?: {
    enabled: boolean
    component?: CompChildren
    text?: BoxTextProps
    dev?: boolean
    onClick?: () => void
  }
  onViewed?: () => void
  triggerOnDataLoaded?: boolean
  dataLoaded?: boolean
}

const Content = ({
  children,
  text,
  textAnim,
  actions,
  footer,
  className = '',
  screenshot,
  onViewed,
  ...props
}: CompProps<Props>) => {
  const [screenshotText, setScreenshotText] = useState<BoxTextProps | null>(
    null
  )
  const [viewed, setViewed] = useStatsBox((s) => [s.viewed, s.setViewed])
  const [isSelfView] = useStats((s) => [s.isSelfView])
  const { isAndroid } = useStatsPermission()
  const showScreenshot = screenshot?.enabled && !isAndroid && isSelfView
  const scope = useRef<HTMLDivElement>(null)
  const [textAnimComplete, setTextAnimComplete] = useState(false)
  const theme = useStatsTheme((s) => s.theme)

  useEffect(() => {
    if (viewed) {
      onViewed?.()
    }
  }, [viewed, onViewed])

  useEffect(() => {
    if (screenshot?.enabled && screenshot?.text) {
      setScreenshotText(screenshot.text)
    } else {
      setScreenshotText(text)
    }
  }, [screenshot?.enabled, screenshot?.text, text])

  useGSAP(
    () => {
      gsap.timeline({
        scrollTrigger: {
          trigger: scope.current,
          start: 'center bottom',
          once: true,
          onToggle: (self) => !viewed && self.isActive && setViewed(true),
        },
      })
    },
    { scope }
  )

  useGSAP(
    () => {
      const isDataTrigger = textAnim?.trigger === 'data'

      const tl = gsap.timeline({
        scrollTrigger: isDataTrigger
          ? null
          : {
              trigger: scope.current,
              start: '-10% bottom',
              once: true,
            },
      })

      if (
        ((isDataTrigger && textAnim.loaded) || !isDataTrigger) &&
        !textAnimComplete
      ) {
        tl.to('.anim__stats-box-text', {
          delay: 0.25,
          opacity: 1,
          x: 0,
          duration: 1,
          ease: 'power2.out',
        })
        setTextAnimComplete(true)
      }
    },
    {
      scope,
      dependencies: [textAnim?.trigger, textAnim?.loaded],
    }
  )

  return (
    <Element name={props.id || ''}>
      <div
        ref={scope}
        className={cx(
          css`
            width: 100%;
            padding: 0 20px;
          `,
          className
        )}
        {...props}
      >
        <div
          className={css`
            padding: 16px;
            width: 100%;
            background-color: ${theme.colors.section.bg};
            border-radius: 24px;
            position: relative;
            transition: background-color 0.3s ease-in-out;
          `}
        >
          <FlexBox
            className={css`
              width: 100%;
            `}
            justifyContent="space-between"
          >
            <div
              className={cx(
                'anim__stats-box-text',
                css`
                  position: relative;
                  z-index: 50;
                  opacity: 0;
                  transform: translateX(-8px);
                  will-change: transform, opacity;
                `
              )}
            >
              <StatsProfileBoxText {...text} />
            </div>
            <FlexBox
              className={css`
                gap: 4px;
              `}
            >
              {actions}
              {showScreenshot ? (
                <StatsScreenshotButton
                  dev={!!screenshot?.dev}
                  onClick={screenshot?.onClick}
                >
                  {screenshotText ? (
                    <StatsProfileBoxText {...screenshotText} hideInfo />
                  ) : null}
                  {screenshot.component}
                </StatsScreenshotButton>
              ) : null}
            </FlexBox>
          </FlexBox>
          <div>
            {children}
            {footer}
          </div>
        </div>
      </div>
    </Element>
  )
}

export const StatsProfileBox = (props: Props) => (
  <StatsBoxProvider>
    <Content {...props} />
  </StatsBoxProvider>
)
